import { toast } from "sonner";
import {
  Trophy,
  Flame,
  Target,
  CheckCircle2,
  AlertCircle,
  Info,
} from "lucide-react";
import React from "react";

interface NotificationOptions {
  duration?: number;
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "top-center"
    | "bottom-center";
}

/**
 * Hook for displaying smart notifications across the app
 * Uses Sonner for toast notifications with custom styling
 */
export const useNotification = () => {
  return {
    // Success notifications
    success: (message: string, options?: NotificationOptions) => {
      toast.success(message, {
        duration: options?.duration || 3000,
        position: options?.position || "top-right",
        className: "bg-green-50 border-green-200 text-green-900",
      });
    },

    // Error notifications
    error: (message: string, options?: NotificationOptions) => {
      toast.error(message, {
        duration: options?.duration || 4000,
        position: options?.position || "top-right",
        className: "bg-red-50 border-red-200 text-red-900",
      });
    },

    // Info notifications
    info: (message: string, options?: NotificationOptions) => {
      toast.info(message, {
        duration: options?.duration || 3000,
        position: options?.position || "top-right",
        className: "bg-blue-50 border-blue-200 text-blue-900",
      });
    },

    // Warning notifications
    warning: (message: string, options?: NotificationOptions) => {
      toast.warning(message, {
        duration: options?.duration || 3500,
        position: options?.position || "top-right",
        className: "bg-amber-50 border-amber-200 text-amber-900",
      });
    },

    // XP Gained notification (bottom center, quick)
    xpGained: (amount: number, options?: NotificationOptions) => {
      toast.custom(
        (t) =>
          React.createElement(
            "div",
            {
              className:
                "bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 font-semibold animate-bounce",
            },
            React.createElement(
              "span",
              { className: "text-lg" },
              `+${amount} XP`,
            ),
            React.createElement("div", {
              className: "w-2 h-2 bg-yellow-300 rounded-full animate-pulse",
            }),
          ),
        {
          duration: options?.duration || 2000,
          position: options?.position || "bottom-center",
        },
      );
    },

    // Task completed notification
    taskCompleted: (
      taskName: string,
      xp: number,
      options?: NotificationOptions,
    ) => {
      toast.custom(
        (t) =>
          React.createElement(
            "div",
            {
              className:
                "bg-card border-2 border-green-200 rounded-xl p-4 shadow-lg flex items-center gap-3 min-w-[300px]",
            },
            React.createElement(
              "div",
              {
                className:
                  "w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0",
              },
              React.createElement(CheckCircle2, {
                className: "w-6 h-6 text-green-600",
              }),
            ),
            React.createElement(
              "div",
              { className: "flex-1" },
              React.createElement(
                "p",
                { className: "font-semibold text-foreground text-sm" },
                "Tarefa Concluída!",
              ),
              React.createElement(
                "p",
                { className: "text-xs text-foreground-secondary" },
                taskName,
              ),
              React.createElement(
                "p",
                { className: "text-xs font-bold text-purple-600 mt-1" },
                `+${xp} XP`,
              ),
            ),
          ),
        {
          duration: options?.duration || 4000,
          position: options?.position || "top-right",
        },
      );
    },

    // Streak milestone notification
    streakMilestone: (days: number, options?: NotificationOptions) => {
      toast.custom(
        (t) =>
          React.createElement(
            "div",
            {
              className:
                "bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-4 shadow-xl flex items-center gap-3 min-w-[300px]",
            },
            React.createElement(
              "div",
              {
                className:
                  "w-12 h-12 bg-card/20 rounded-lg flex items-center justify-center flex-shrink-0",
              },
              React.createElement(Flame, {
                className: "w-7 h-7 text-white",
              }),
            ),
            React.createElement(
              "div",
              { className: "flex-1" },
              React.createElement(
                "p",
                { className: "font-bold text-lg" },
                `${days} Dias de Fogo!`,
              ),
              React.createElement(
                "p",
                { className: "text-sm text-white/90" },
                "Você está imparável!",
              ),
            ),
          ),
        {
          duration: options?.duration || 5000,
          position: options?.position || "top-center",
        },
      );
    },

    // Temple unlocked notification
    templeUnlocked: (templeName: string, options?: NotificationOptions) => {
      toast.custom(
        (t) =>
          React.createElement(
            "div",
            {
              className:
                "bg-gradient-to-br from-accent-700 to-accent-900 text-white rounded-xl p-4 shadow-xl flex items-center gap-3 min-w-[320px] border-2 border-accent-500",
            },
            React.createElement(
              "div",
              {
                className:
                  "w-12 h-12 bg-card/20 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse",
              },
              React.createElement(Target, {
                className: "w-7 h-7 text-white",
              }),
            ),
            React.createElement(
              "div",
              { className: "flex-1" },
              React.createElement(
                "p",
                { className: "font-bold text-base mb-1" },
                "Novo Templo Desbloqueado!",
              ),
              React.createElement(
                "p",
                { className: "text-sm text-accent-100" },
                templeName,
              ),
            ),
          ),
        {
          duration: options?.duration || 5000,
          position: options?.position || "top-center",
        },
      );
    },

    // Custom notification with icon
    custom: (
      title: string,
      message: string,
      icon: "trophy" | "flame" | "target" | "check" | "alert" | "info" = "info",
      options?: NotificationOptions,
    ) => {
      const iconMap = {
        trophy: Trophy,
        flame: Flame,
        target: Target,
        check: CheckCircle2,
        alert: AlertCircle,
        info: Info,
      };

      const colorMap = {
        trophy: "from-amber-500 to-yellow-500",
        flame: "from-orange-500 to-red-500",
        target: "from-accent-700 to-accent-900",
        check: "from-green-500 to-emerald-600",
        alert: "from-red-500 to-rose-600",
        info: "from-blue-500 to-indigo-600",
      };

      const IconComponent = iconMap[icon];
      const gradient = colorMap[icon];

      toast.custom(
        (t) =>
          React.createElement(
            "div",
            {
              className: `bg-gradient-to-r ${gradient} text-white rounded-xl p-4 shadow-xl flex items-center gap-3 min-w-[300px]`,
            },
            React.createElement(
              "div",
              {
                className:
                  "w-10 h-10 bg-card/20 rounded-lg flex items-center justify-center flex-shrink-0",
              },
              React.createElement(IconComponent, {
                className: "w-6 h-6 text-white",
              }),
            ),
            React.createElement(
              "div",
              { className: "flex-1" },
              React.createElement(
                "p",
                { className: "font-bold text-sm" },
                title,
              ),
              React.createElement(
                "p",
                { className: "text-sm text-white/90" },
                message,
              ),
            ),
          ),
        {
          duration: options?.duration || 4000,
          position: options?.position || "top-right",
        },
      );
    },
  };
};
